// Amazon Associates Integration Service
// Note: Amazon deprecated their Product Advertising API for new applications
// We'll use alternative approaches for product search and affiliate links

const AMAZON_ASSOCIATE_TAG = process.env.REACT_APP_AMAZON_ASSOCIATE_TAG || "givvio-20"

// Amazon search domains by country
const AMAZON_DOMAINS = {
  US: "amazon.com",
  UK: "amazon.co.uk",
  CA: "amazon.ca",
  DE: "amazon.de",
  FR: "amazon.fr",
  IT: "amazon.it",
  ES: "amazon.es",
  JP: "amazon.co.jp",
  IN: "amazon.in",
  AU: "amazon.com.au",
}

// Generate Amazon affiliate link
export const generateAffiliateLink = (asin, country = "US") => {
  const domain = AMAZON_DOMAINS[country]
  return `https://${domain}/dp/${asin}?tag=${AMAZON_ASSOCIATE_TAG}`
}

// Generate Amazon search link with affiliate tag
export const generateSearchLink = (searchTerm, country = "US") => {
  const domain = AMAZON_DOMAINS[country]
  const encodedTerm = encodeURIComponent(searchTerm)
  return `https://${domain}/s?k=${encodedTerm}&tag=${AMAZON_ASSOCIATE_TAG}`
}

// Extract ASIN from Amazon URL
export const extractASIN = amazonUrl => {
  const asinRegex = /\/dp\/([A-Z0-9]{10})|\/gp\/product\/([A-Z0-9]{10})|asin=([A-Z0-9]{10})/i
  const match = amazonUrl.match(asinRegex)
  return match ? match[1] || match[2] || match[3] : null
}

// Real Amazon product search using custom scraper
export const searchAmazonProducts = async (searchTerm, options = {}) => {
  const { country = "US", limit = 10 } = options

  try {
    console.log(`🔍 Searching Amazon for: ${searchTerm}`)

    // Call our custom scraper API
    const baseUrl = process.env.NODE_ENV === "production" ? "" : "http://localhost:3001"

    const response = await fetch(`${baseUrl}/api/scrape-amazon?query=${encodeURIComponent(searchTerm)}&type=search&limit=${limit}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`Scraper request failed: ${response.status}`)
    }

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.error || "Scraping failed")
    }

    // Transform scraper response to our format - only include products with real data
    const products = (data.products || [])
      .filter(product => product.asin && product.title) // Only include products with real ASIN and title
      .map(product => ({
        asin: product.asin,
        title: product.title,
        price: product.price || "See on Amazon",
        rating: parseFloat(product.rating) || 0,
        reviewCount: parseInt(product.reviewCount) || 0,
        imageUrl: product.imageUrl || "",
        affiliateLink: generateAffiliateLink(product.asin, country),
        isPrime: product.isPrime || false,
        category: product.category || "General",
        url: product.url,
        product_url: product.url,
      }))

    console.log(`Found ${products.length} products for: ${searchTerm}`)

    return {
      success: true,
      products: products.slice(0, limit),
      searchTerm,
      totalResults: data.totalResults || products.length,
    }
  } catch (error) {
    console.error("Amazon product search failed:", error)

    // Return empty result - no fallbacks
    return {
      success: false,
      error: error.message,
      products: [],
      searchTerm,
      totalResults: 0,
    }
  }
}

// Get product details by ASIN using custom scraper
export const getProductDetails = async (asin, country = "US") => {
  try {
    console.log(`Getting product details for ASIN: ${asin}`)

    // Call our custom scraper API
    const baseUrl = process.env.NODE_ENV === "production" ? "" : "http://localhost:3001"

    const response = await fetch(`${baseUrl}/api/scrape-amazon?asin=${asin}&type=product`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`Scraper request failed: ${response.status}`)
    }

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.error || "Product scraping failed")
    }

    // Transform scraper response
    const product = {
      asin: data.product?.asin || asin,
      title: data.product?.title,
      description: data.product?.description,
      price: data.product?.price,
      originalPrice: data.product?.originalPrice,
      rating: parseFloat(data.product?.rating) || 0,
      reviewCount: parseInt(data.product?.reviewCount) || 0,
      imageUrl: data.product?.imageUrl,
      product_photo: data.product?.imageUrl,
      affiliateLink: generateAffiliateLink(asin, country),
      isPrime: data.product?.isPrime || false,
      is_prime: data.product?.isPrime || false,
      inStock: data.product?.inStock !== false,
      category: data.product?.category || "General",
      url: data.product?.url,
      product_url: data.product?.url,
    }

    console.log(`Successfully scraped product details for: ${asin}`)

    return {
      success: true,
      product,
    }
  } catch (error) {
    console.error("Get product details error:", error)

    // Don't use fallback - return error
    return {
      success: false,
      error: error.message,
      product: null,
    }
  }
}

// Track affiliate clicks (for analytics)
export const trackAffiliateClick = async (asin, searchTerm, userId = null) => {
  try {
    // In production, send this data to your analytics service
    const clickData = {
      asin,
      searchTerm,
      userId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    }

    console.log("Affiliate click tracked:", clickData)

    // You could send this to Supabase, Google Analytics, or your own tracking service
    // await supabase.from('affiliate_clicks').insert(clickData)

    return { success: true }
  } catch (error) {
    console.error("Track affiliate click error:", error)
    return { success: false, error: error.message }
  }
}

// Validate Amazon Associate tag
export const validateAssociateTag = tag => {
  // Amazon Associate tags are typically 3-20 characters, alphanumeric with hyphens
  const tagRegex = /^[a-zA-Z0-9-]{3,20}$/
  return tagRegex.test(tag)
}

const amazonService = {
  generateAffiliateLink,
  generateSearchLink,
  extractASIN,
  searchAmazonProducts,
  getProductDetails,
  trackAffiliateClick,
  validateAssociateTag,
  AMAZON_DOMAINS,
}

export default amazonService
